import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBriefcase,
  FiUser,
  FiMail,
  FiPhone,
  FiPackage,
  FiDollarSign,
  FiFileText,
  FiAlertTriangle,
  FiTrendingUp,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiXCircle,
} from 'react-icons/fi';
import { lraService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatCard from '../../components/StatCard';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';

const BusinessProfile = () => {
  const { dssn } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery(['lra-business-profile', dssn], () =>
    lraService.getBusinessProfile(dssn)
  );

  if (isLoading) return <LoadingSpinner message="Loading business profile..." />;

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className="icon-btn"
              onClick={() => navigate('/lra/businesses')}
            >
              <FiArrowLeft size={20} />
            </button>
            <h1 className="page-title">Business Not Found</h1>
          </div>
        </div>
        <div className="card">
          <div className="empty-state">
            <FiAlertTriangle size={48} color="#ef4444" />
            <h3>Unable to load business profile</h3>
            <p>{error.message}</p>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/lra/businesses')}
            >
              Back to Directory
            </button>
          </div>
        </div>
      </div>
    );
  }

  const profile = data?.data || {};
  const business = profile.business || {};
  const owner = profile.owner || {};
  const sales = profile.sales || {};
  const stock = profile.stock || {};
  const filings = profile.filings || {};
  const signals = profile.audit_signals || [];

  const openSignals = signals.filter((s) => s.status === 'open').length;
  const highRiskSignals = signals.filter(
    (s) => s.severity === 'high' && s.status !== 'cleared'
  ).length;

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'sales', label: 'Sales' },
    { id: 'filings', label: 'Filings' },
    { id: 'signals', label: `Audit Signals (${signals.length})` },
  ];

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            className="icon-btn"
            onClick={() => navigate('/lra/businesses')}
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">{business.business_name}</h1>
            <p
              className="page-subtitle"
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            >
              DSSN: {business.dssn} • TIN: {business.tin || '—'}
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status strip */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
        }}
      >
        <StatusChip
          label="Verification"
          value={business.verification_status || 'pending'}
          color={
            business.verification_status === 'verified'
              ? 'green'
              : business.verification_status === 'rejected'
              ? 'red'
              : 'orange'
          }
        />
        <StatusChip
          label="Division"
          value={(business.tax_division || 'unclassified').replace(/_/g, ' ')}
          color="blue"
        />
        <StatusChip
          label="Open Signals"
          value={openSignals}
          color={openSignals > 0 ? 'orange' : 'green'}
        />
        <StatusChip
          label="High Risk"
          value={highRiskSignals}
          color={highRiskSignals > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Stat cards */}
      <div className="stats-grid">
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={formatCurrency(sales.total_sales || 0)}
          label="Total Sales"
          color="green"
        />
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={formatCurrency(sales.last_30d?.amount || 0)}
          label="Last 30 Days"
          color="blue"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={formatCurrency(filings.total_tax_due || 0)}
          label="Total Tax Filed"
          color="purple"
        />
        <StatCard
          icon={<FiPackage size={24} />}
          value={stock.total_products || 0}
          label="Products"
          color="orange"
        />
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginTop: '24px',
          marginBottom: '16px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom:
                activeTab === tab.id
                  ? '2px solid #1a73e8'
                  : '2px solid transparent',
              color: activeTab === tab.id ? '#1a73e8' : '#6b7280',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '-1px',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          business={business}
          owner={owner}
          sales={sales}
          stock={stock}
          filings={filings}
        />
      )}

      {activeTab === 'sales' && <SalesTab sales={sales} />}

      {activeTab === 'filings' && <FilingsTab filings={filings} />}

      {activeTab === 'signals' && (
        <SignalsTab
          signals={signals}
          onNavigate={(id) => navigate(`/lra/audit-signals?id=${id}`)}
        />
      )}
    </div>
  );
};

// ---------- TAB: Overview ----------
const OverviewTab = ({ business, owner, sales, stock, filings }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
      gap: '24px',
    }}
  >
    {/* Business info */}
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Business Information</h2>
      </div>
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="Business Name"
        value={business.business_name}
      />
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="Business ID"
        value={business.business_id}
        mono
      />
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="TIN"
        value={business.tin || '—'}
        mono
      />
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="DSSN"
        value={business.dssn}
        mono
      />
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="Tax Division"
        value={(business.tax_division || 'unclassified').replace(/_/g, ' ')}
      />
      <InfoRow
        icon={<FiBriefcase size={16} />}
        label="Verification"
        value={business.verification_status}
      />
      <InfoRow
        icon={<FiClock size={16} />}
        label="Registered"
        value={formatDate(business.created_at)}
      />
      {business.verified_at && (
        <InfoRow
          icon={<FiCheckCircle size={16} />}
          label="Verified"
          value={formatDate(business.verified_at)}
        />
      )}
    </div>

    {/* Owner info */}
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Owner</h2>
      </div>
      {!owner.first_name ? (
        <div className="empty-state">
          <FiUser size={40} />
          <p>Owner information not available</p>
        </div>
      ) : (
        <>
          <InfoRow
            icon={<FiUser size={16} />}
            label="Name"
            value={`${owner.first_name} ${owner.last_name || ''}`.trim()}
          />
          <InfoRow
            icon={<FiMail size={16} />}
            label="Email"
            value={owner.email || '—'}
          />
          <InfoRow
            icon={<FiPhone size={16} />}
            label="Phone"
            value={owner.phone || '—'}
          />
        </>
      )}
    </div>

    {/* Sales summary */}
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Sales Summary</h2>
      </div>
      <InfoRow
        label="Total Sales"
        value={formatCurrency(sales.total_sales || 0)}
        bold
      />
      <InfoRow
        label="Total Transactions"
        value={sales.total_transactions || 0}
      />
      <InfoRow
        label="Total Tax"
        value={formatCurrency(sales.total_tax || 0)}
      />
      <InfoRow
        label="Last 30 Days"
        value={`${formatCurrency(sales.last_30d?.amount || 0)} (${
          sales.last_30d?.count || 0
        } txns)`}
      />
      <InfoRow
        label="First Sale"
        value={sales.first_sale ? formatDate(sales.first_sale) : '—'}
      />
      <InfoRow
        label="Last Sale"
        value={sales.last_sale ? formatDate(sales.last_sale) : '—'}
      />
    </div>

    {/* Stock & filings */}
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Stock & Filings</h2>
      </div>
      <InfoRow label="Products" value={stock.total_products || 0} />
      <InfoRow label="Units in Stock" value={stock.total_units || 0} />
      <InfoRow
        label="Stock Value"
        value={formatCurrency(stock.stock_value_usd || 0)}
      />
      <div style={{ borderTop: '1px solid #f3f4f6', margin: '8px 0' }} />
      <InfoRow label="Total Filings" value={filings.total || 0} />
      <InfoRow label="Drafts" value={filings.drafts || 0} />
      <InfoRow label="Submitted" value={filings.submitted || 0} />
      <InfoRow label="Approved" value={filings.approved || 0} />
      <InfoRow
        label="Total Tax Due"
        value={formatCurrency(filings.total_tax_due || 0)}
        bold
      />
    </div>
  </div>
);

// ---------- TAB: Sales ----------
const SalesTab = ({ sales }) => (
  <div className="card">
    <div className="card-header">
      <h2 className="card-title">Sales Details</h2>
    </div>
    {!sales.total_transactions ? (
      <div className="empty-state">
        <FiTrendingUp size={40} />
        <p>No sales recorded yet</p>
      </div>
    ) : (
      <>
        <InfoRow
          label="Total Sales"
          value={formatCurrency(sales.total_sales || 0)}
          bold
        />
        <InfoRow
          label="Total Transactions"
          value={sales.total_transactions}
        />
        <InfoRow
          label="Total Tax Collected"
          value={formatCurrency(sales.total_tax || 0)}
        />
        <InfoRow
          label="Average Sale"
          value={formatCurrency(
            (sales.total_sales || 0) / (sales.total_transactions || 1)
          )}
        />
        <InfoRow
          label="Last 30 Days Sales"
          value={formatCurrency(sales.last_30d?.amount || 0)}
        />
        <InfoRow
          label="Last 30 Days Transactions"
          value={sales.last_30d?.count || 0}
        />
        <InfoRow
          label="First Sale"
          value={sales.first_sale ? formatDateTime(sales.first_sale) : '—'}
        />
        <InfoRow
          label="Last Sale"
          value={sales.last_sale ? formatDateTime(sales.last_sale) : '—'}
        />
      </>
    )}
  </div>
);

// ---------- TAB: Filings ----------
const FilingsTab = ({ filings }) => (
  <div className="card">
    <div className="card-header">
      <h2 className="card-title">Tax Filings</h2>
    </div>
    {!filings.total ? (
      <div className="empty-state">
        <FiFileText size={40} />
        <p>No filings recorded yet</p>
      </div>
    ) : (
      <div className="stats-grid" style={{ marginTop: 0 }}>
        <StatCard
          icon={<FiFileText size={24} />}
          value={filings.total || 0}
          label="Total Filings"
          color="blue"
        />
        <StatCard
          icon={<FiClock size={24} />}
          value={filings.drafts || 0}
          label="Drafts"
          color="orange"
        />
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={filings.submitted || 0}
          label="Submitted"
          color="purple"
        />
        <StatCard
          icon={<FiCheckCircle size={24} />}
          value={filings.approved || 0}
          label="Approved"
          color="green"
        />
      </div>
    )}
  </div>
);

// ---------- TAB: Signals ----------
const SignalsTab = ({ signals, onNavigate }) => (
  <div className="card">
    <div className="card-header">
      <h2 className="card-title">Audit Signals</h2>
    </div>
    {signals.length === 0 ? (
      <div className="empty-state">
        <FiCheckCircle size={40} color="#22c55e" />
        <h3>No audit signals</h3>
        <p>This business is clean — no flags have been raised.</p>
      </div>
    ) : (
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Title</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Detected</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {signals.map((s) => (
              <tr key={s.signal_id}>
                <td>
                  <code style={{ fontSize: '11px' }}>
                    {s.signal_type}
                  </code>
                </td>
                <td>{s.title}</td>
                <td>
                  <SeverityBadge severity={s.severity} />
                </td>
                <td>{s.status}</td>
                <td>{formatDate(s.detected_at)}</td>
                <td>
                  <button
                    className="icon-btn"
                    onClick={() => onNavigate(s.signal_id)}
                  >
                    <FiAlertTriangle size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ---------- Helper components ----------
const InfoRow = ({ icon, label, value, bold, mono }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid #f3f4f6',
    }}
  >
    <span
      style={{
        color: '#6b7280',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {icon}
      {label}
    </span>
    <span
      style={{
        fontWeight: bold ? 700 : 500,
        fontSize: mono ? '12px' : '14px',
        fontFamily: mono ? 'monospace' : 'inherit',
        color: '#1f2937',
      }}
    >
      {value}
    </span>
  </div>
);

const StatusChip = ({ label, value, color }) => {
  const colors = {
    green: { bg: '#dcfce7', text: '#166534' },
    orange: { bg: '#fef3c7', text: '#92400e' },
    red: { bg: '#fee2e2', text: '#991b1b' },
    blue: { bg: '#dbeafe', text: '#1e40af' },
  };
  const c = colors[color] || colors.blue;
  return (
    <div
      style={{
        background: c.bg,
        color: c.text,
        padding: '8px 14px',
        borderRadius: '8px',
        fontSize: '13px',
      }}
    >
      <span style={{ opacity: 0.75 }}>{label}:</span>{' '}
      <strong style={{ textTransform: 'capitalize' }}>{value}</strong>
    </div>
  );
};

const SeverityBadge = ({ severity }) => {
  const colors = {
    high: { bg: '#fee2e2', color: '#991b1b' },
    medium: { bg: '#fef3c7', color: '#92400e' },
    low: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const c = colors[severity] || colors.low;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {severity}
    </span>
  );
};

export default BusinessProfile;
